import { useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { ActivityFeedButton } from "@/components/domain/activity-feed-button"
import { WorkflowYamlEditor } from "@/components/domain/workflow-yaml-editor"
import type { SharedProps } from "@/types/inertia"
import type { CompletionRatingPolicy, SkillEditable, SkillVersionRow, ToolCatalogEntry } from "@/types/skill"

type Props = {
  skill: SkillEditable
  toolCatalog: ToolCatalogEntry[]
  versions: SkillVersionRow[]
}

export default function SkillsEdit({ skill, versions }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash
  const isLive    = skill.live
  const isDraft   = skill.state === "draft"
  const isGlobal  = skill.isGlobal
  // Only drafts are mutable. Live and archived skills are read-only —
  // the operator must Create Draft to make changes. Globals are
  // read-only at the company layer regardless of state.
  const writable  = !isGlobal && isDraft

  const [name,        setName]        = useState(skill.name)
  const [slug,        setSlug]        = useState(skill.key)
  const [description, setDescription] = useState(skill.description ?? "")
  const [notes,       setNotes]       = useState(skill.notes ?? "")
  const [workflowYaml, setWorkflowYaml] = useState(skill.workflowYaml ?? "")
  const [position,    setPosition]    = useState(skill.position)
  const [submitting, setSubmitting] = useState(false)

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting || !writable) return
    setSubmitting(true)
    const payload = {
      skill: {
        name,
        key: slug,
        description,
        notes,
        workflow_yaml: workflowYaml,
        position,
      },
    }
    router.patch(`/skills/${skill.id}`, payload, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    })
  }

  function createDraft() {
    if (!confirm(`Clone v${skill.version} into a new draft?`)) return
    router.post(`/skills/${skill.id}/draft_version`, {}, { preserveScroll: false })
  }

  function promote() {
    if (!confirm(`Promote v${skill.version} to live? Production traffic switches over immediately.`)) return
    router.patch(`/skills/${skill.id}/promote`, { skill_version_id: skill.versionId }, { preserveScroll: false })
  }

  function discard() {
    if (!confirm("Discard this draft? Production is not affected.")) return
    router.delete(`/skills/${skill.id}`, { preserveScroll: false })
  }

  function archiveSkill() {
    if (!confirm("Archive this skill? It will stop running and be removed from attached agents.")) return
    router.post(`/skills/${skill.id}/archive`, {}, { preserveScroll: false })
  }

  function updateCompletionRatingPolicy(agentSkillId: number, policy: CompletionRatingPolicy) {
    if (submitting) return
    setSubmitting(true)
    router.patch(
      `/skills/${skill.id}/agent_assignments/${agentSkillId}/completion_rating_policy`,
      { completion_rating_policy: policy },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/skills" className="hover:text-app-text">Skills</Link>}
        title={`Edit ${skill.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              key <code className="rounded bg-white/5 px-1 text-xs">{skill.key}</code>
              {" · "}
              {skill.agentAssignments.length === 0
                ? "no agents attached"
                : `attached to ${skill.agentAssignments.length} agent${skill.agentAssignments.length === 1 ? "" : "s"}`}
            </span>
            <Badge tone={isLive ? "gold" : isDraft ? "red" : "neutral"}>
              v{skill.version} {skill.state}
            </Badge>
            {isGlobal && <Badge tone="neutral">Global</Badge>}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ActivityFeedButton entries={skill.editHistory ?? []} title="Skill activity" />
            <Link href={`/skills/${skill.id}/test?tab=design`}>
              <Button type="button" variant="gold" size="sm">
                Open Designer →
              </Button>
            </Link>
            {isGlobal && (
              <Button
                type="button"
                variant="red"
                size="sm"
                onClick={() => {
                  if (!confirm("Clone this global skill into your company? You'll get a draft you can edit.")) return
                  router.post(`/skills/${skill.id}/clone`, {}, { preserveScroll: false })
                }}
              >
                Clone to my company
              </Button>
            )}
            {!isGlobal && isDraft && (
              <Button type="button" variant="gold" size="sm" onClick={promote}>
                Promote to live
              </Button>
            )}
            {!isGlobal && isDraft && (
              <Button type="button" variant="ghost" size="sm" onClick={discard}>
                Discard draft
              </Button>
            )}
            {!isGlobal && !isDraft && (
              <Button type="button" variant="red" size="sm" onClick={createDraft}>
                Create draft v{skill.version + 1}
              </Button>
            )}
            {!isGlobal && skill.state !== "archived" && (
              <Button type="button" variant="ghost" size="sm" onClick={archiveSkill}>
                Archive skill
              </Button>
            )}
          </div>
        }
      />

      {isGlobal && (
        <div className="rounded-control border border-border bg-white/5 px-4 py-2 text-sm text-app-muted">
          This is a global library skill. It's read-only — clone it into your company
          to customize.
        </div>
      )}

      {!isGlobal && !isDraft && (
        <div className="rounded-control border border-vip-gold/30 bg-vip-goldMute px-4 py-2 text-sm text-vip-goldSoft">
          v{skill.version} is {skill.state} — read-only. Use
          <strong className="mx-1 text-app-text">Create draft v{skill.version + 1}</strong>
          to make changes.
        </div>
      )}

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <form onSubmit={save} className="space-y-5">
        <fieldset disabled={!writable} className="space-y-5 disabled:opacity-60">
        <Card className="space-y-5 p-6">
          <header>
            <h2 className="text-lg font-semibold">Basics</h2>
            <p className="text-sm text-app-muted">Identity and notes.</p>
          </header>

          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Name" hint="Shown in the skills catalog and selected-agent panel.">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>

            <Field
              label="Key"
              hint={isDraft
                ? "snake_case slug — editable until first promote."
                : "Read-only — this is where this skill plugs into the prompt and analytics."}
            >
              {isDraft ? (
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                  required
                />
              ) : (
                <code className="block rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-sm text-app-muted">
                  {skill.key}
                </code>
              )}
            </Field>

            <Field label="Position" hint="Sort order in the agent's skill list (lower = earlier).">
              <Input
                type="number"
                value={position}
                onChange={(e) => setPosition(parseInt(e.target.value, 10) || 0)}
                min={0}
              />
            </Field>

          </div>

          <Field label="Description" hint="Short blurb. Surfaces on the catalog row and tooltip.">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </Field>

          <Field
            label="Release notes"
            hint="One-line summary shown in the Versions table (e.g. 'Added banned-user guard')."
          >
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed in this version?"
            />
          </Field>
        </Card>

        {versions.length > 1 && (
          <Card className="space-y-3 p-6">
            <header>
              <h2 className="text-lg font-semibold">Versions</h2>
              <p className="text-sm text-app-muted">{versions.length} versions in this lineage. Promote any version to roll forward or back.</p>
            </header>

            {/* md+ table view. Below md the same data renders as a
                stacked card list (next block) — desktops keep the
                scannable grid; phones avoid horizontal overflow. */}
            <div className="hidden overflow-hidden rounded-control border border-border md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-white/5 text-xs uppercase tracking-wide text-app-subtle">
                    <th className="px-3 py-2 text-left">Version</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Updated</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {versions.map((v) => {
                    // Post-SkillVersion-refactor: row id is a SkillVersion
                    // id, the working/edit row is whichever version the
                    // presenter resolved to (skill.versionId). Edit links
                    // still point at the parent Skill; promote carries the
                    // selected SkillVersion id so multiple drafts behave.
                    const here = v.id === skill.versionId
                    return (
                      <tr key={v.id} className={here ? "bg-brand-redSoft/30" : ""}>
                        <td className="px-3 py-2 font-medium">v{v.version}</td>
                        <td className="px-3 py-2">
                          <Badge tone={v.status === "live" ? "gold" : v.status === "draft" ? "red" : "neutral"}>
                            {v.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-app-muted">
                          {new Date(v.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td className="px-3 py-2 text-app-muted">{v.notes ?? "—"}</td>
                        <td className="px-3 py-2 text-right">
                          {!here && v.status === "draft" && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirm(`Promote v${v.version} to live?`)) return
                                router.patch(`/skills/${skill.id}/promote`, { skill_version_id: v.id }, { preserveScroll: false })
                              }}
                              className="text-xs text-brand-red hover:underline"
                            >
                              Promote
                            </button>
                          )}
                          {here && <span className="text-xs text-app-subtle">current</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — stacked rows so the version metadata
                stays readable on a phone. */}
            <ul className="space-y-2 md:hidden">
              {versions.map((v) => {
                const here = v.id === skill.versionId
                return (
                  <li
                    key={v.id}
                    className={[
                      "rounded-control border border-border p-3",
                      here ? "bg-brand-redSoft/30" : "bg-white/[0.02]",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">v{v.version}</span>
                      <Badge tone={v.status === "live" ? "gold" : v.status === "draft" ? "red" : "neutral"}>
                        {v.status}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-app-muted">
                      Updated {new Date(v.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                    {v.notes && (
                      <div className="mt-1 text-xs text-app-muted">{v.notes}</div>
                    )}
                    <div className="mt-2">
                      {!here && v.status === "draft" && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!confirm(`Promote v${v.version} to live?`)) return
                            router.patch(`/skills/${skill.id}/promote`, { skill_version_id: v.id }, { preserveScroll: false })
                          }}
                          className="text-xs text-brand-red hover:underline"
                        >
                          Promote
                        </button>
                      )}
                      {here && <span className="text-xs text-app-subtle">current</span>}
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        )}

        <Card className="space-y-3 p-6">
          <header>
            <h2 className="text-lg font-semibold">Score Card YAML</h2>
            <p className="text-sm text-app-muted">
              Canonical behavior for this skill. The trigger, prompt, descriptive
              steps, fields, data loaders, policies, files, and actions live here.
            </p>
          </header>

          <WorkflowYamlEditor
            value={workflowYaml}
            savedValue={skill.workflowYaml ?? ""}
            toolKeys={skill.derivedToolKeys ?? []}
            onChange={setWorkflowYaml}
            workflowScope="skill"
            skillId={skill.id}
            readOnly={!writable}
            minHeight="520px"
            footerHint={writable ? "Changes save with the skill form." : "This version is read-only. Create a draft to edit the Score Card."}
            toolHeading="Derived tools"
            placeholder={`dsl_version: skill_card_v3
reply:
  guidance: >
    Use the current WorkflowV3 state to answer clearly and ask for any
    missing required context.
trigger:
  description: "User needs help with ${skill.name || skill.key}."
  examples:
    - "I need help with ${skill.name || skill.key}"

prompt: >
  Help the user with ${skill.name || skill.key}. Be concise, friendly, and
  grounded only in the current Score Card state.

steps:
  - key: collect_required_context
    description: >
      First collect the required fields. Until they are confirmed, ask for the
      missing or unclear value and do not claim that any side effect happened.

data:
  loaders: {}

fields: {}

policies: {}

files: {}

actions: {}`}
          />
        </Card>

        </fieldset>

        {!isGlobal && skill.agentAssignments.length > 0 && (
          <Card className="space-y-3 p-6">
            <header>
              <h2 className="text-lg font-semibold">Attached agents</h2>
              <p className="text-sm text-app-muted">
                Override the platform completion rating policy for this skill on each agent.
              </p>
            </header>
            <div className="overflow-x-auto rounded-control border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-white/5 text-xs uppercase tracking-wide text-app-subtle">
                    <th className="px-3 py-2 text-left">Agent</th>
                    <th className="px-3 py-2 text-left">Key</th>
                    <th className="px-3 py-2 text-left">Completion rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {skill.agentAssignments.map((assignment) => (
                    <tr key={assignment.agentSkillId}>
                      <td className="px-3 py-2 font-medium text-app-text">{assignment.agentName}</td>
                      <td className="px-3 py-2">
                        <code className="text-xs text-app-muted">{assignment.agentKey}</code>
                      </td>
                      <td className="px-3 py-2">
                        <label className="sr-only" htmlFor={`completion-rating-policy-${assignment.agentSkillId}`}>
                          Completion rating policy
                        </label>
                        <select
                          id={`completion-rating-policy-${assignment.agentSkillId}`}
                          value={assignment.completionRatingPolicy}
                          onChange={(e) =>
                            updateCompletionRatingPolicy(
                              assignment.agentSkillId,
                              e.target.value as CompletionRatingPolicy,
                            )
                          }
                          disabled={submitting}
                          className="rounded-control border border-border bg-white/5 px-2 py-1 text-xs text-app-text outline-none transition focus:border-brand-red/50"
                        >
                          <option value="inherit">Inherit agent default</option>
                          <option value="on">Always ask</option>
                          <option value="off">Never ask</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link
            href={`/skills/${skill.id}`}
            className="inline-flex w-full items-center justify-center rounded-control border border-border bg-transparent px-4 py-2 text-sm font-medium text-app-text transition hover:bg-white/5 sm:w-auto"
          >
            Cancel
          </Link>
          {writable && (
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          )}
        </div>
      </form>
    </AppShell>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-app-text">{label}</div>
      {hint && <div className="mb-2 text-xs text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}
